/**
 * Industry Taxonomy (Dashboard Copy)
 *
 * This is a frontend-compatible copy of the industry taxonomy.
 * The source of truth is in niftee-orchestrator/src/constants/industry-taxonomy.ts
 *
 * IMPORTANT: Keep these in sync! If you update one, update the other.
 */

import type { SectionType } from './section-types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Industry-specific question for onboarding Step 7
 */
export interface IndustryQuestion {
  id: string;
  label: string;
  placeholder?: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'boolean';
  options?: string[];
  required?: boolean;
  hint?: string;
}

/**
 * Specific industry type within a primary category
 */
export interface IndustryType {
  id: string;
  label: string;
  /** Additional questions specific to this industry type */
  questions?: IndustryQuestion[];
  /** Keywords for AI profile generation */
  keywords?: string[];
  /** Suggested credentials/licenses for this industry */
  suggestedCredentials?: string[];
}

/**
 * Primary industry category
 */
export interface IndustryCategory {
  id: string;
  label: string;
  description: string;
  icon?: string;
  /** Section order for websites in this category */
  flowPattern: SectionType[];
  /** Specific industry types within this category */
  types: IndustryType[];
  /** Common questions for all types in this category */
  commonQuestions?: IndustryQuestion[];
  /** Section label overrides for this category */
  sectionLabels?: Partial<Record<SectionType, string>>;
  /** Whether credentials section should be prominent */
  credentialsImportant?: boolean;
  /** Gallery label override */
  galleryLabel?: string;
  /** Services label override */
  servicesLabel?: string;
}

// =============================================================================
// INDUSTRY TAXONOMY
// =============================================================================

export const INDUSTRY_TAXONOMY: IndustryCategory[] = [
  // -------------------------------------------------------------------------
  // HOME SERVICES & CONTRACTORS
  // -------------------------------------------------------------------------
  {
    id: 'contractor',
    label: 'Home Services & Contractors',
    description: 'Construction, renovation, and home improvement services',
    icon: 'Wrench',
    flowPattern: ['hero', 'services', 'gallery', 'credentials', 'testimonials', 'process', 'about', 'faq', 'cta', 'contact'],
    credentialsImportant: true,
    galleryLabel: 'Our Work',
    servicesLabel: 'Services',
    sectionLabels: {
      credentials: 'Licenses & Certifications',
      gallery: 'Recent Projects',
    },
    commonQuestions: [
      {
        id: 'license_number',
        label: 'License Number (if applicable)',
        type: 'text',
        placeholder: 'e.g., #12345678',
        hint: 'Your contractor license number for display on your website',
      },
      {
        id: 'service_area',
        label: 'Service Area',
        type: 'text',
        placeholder: 'e.g., Greater Austin area, Travis and Williamson counties',
        required: true,
      },
      {
        id: 'years_experience',
        label: 'Years of Experience',
        type: 'text',
        placeholder: 'e.g., 15',
        hint: 'Your personal experience in this trade (may differ from business age)',
      },
      {
        id: 'license_status',
        label: 'Select all that apply to your business:',
        type: 'multiselect',
        options: ['Licensed', 'Bonded', 'Insured'],
      },
    ],
    types: [
      {
        id: 'general-contractor',
        label: 'General Contractor',
        keywords: ['construction', 'renovation', 'remodel', 'building'],
        suggestedCredentials: ['General Contractor License', 'Bonded & Insured', 'OSHA Certified'],
      },
      {
        id: 'electrician',
        label: 'Electrician',
        keywords: ['electrical', 'wiring', 'panel', 'lighting'],
        suggestedCredentials: ['Master Electrician License', 'Journeyman License', 'Bonded & Insured'],
      },
      {
        id: 'plumber',
        label: 'Plumber',
        keywords: ['plumbing', 'pipes', 'water heater', 'drain'],
        suggestedCredentials: ['Master Plumber License', 'Journeyman License', 'Bonded & Insured'],
      },
      {
        id: 'hvac',
        label: 'HVAC Technician',
        keywords: ['heating', 'cooling', 'air conditioning', 'ventilation'],
        suggestedCredentials: ['HVAC License', 'EPA 608 Certification', 'NATE Certified'],
      },
      {
        id: 'roofer',
        label: 'Roofing Contractor',
        keywords: ['roofing', 'shingles', 'roof repair', 'gutters'],
        suggestedCredentials: ['Roofing License', 'GAF Certified', 'Bonded & Insured'],
      },
      {
        id: 'painter',
        label: 'Painter',
        keywords: ['painting', 'interior', 'exterior', 'staining'],
        suggestedCredentials: ['EPA Lead-Safe Certified', 'Bonded & Insured'],
      },
      {
        id: 'landscaper',
        label: 'Landscaper',
        keywords: ['landscaping', 'lawn care', 'garden', 'irrigation'],
        suggestedCredentials: ['Landscape Contractor License', 'Pesticide Applicator License'],
      },
      {
        id: 'handyman',
        label: 'Handyman',
        keywords: ['handyman', 'repairs', 'maintenance', 'odd jobs'],
        suggestedCredentials: ['Bonded & Insured'],
      },
      {
        id: 'pool-service',
        label: 'Pool Service',
        keywords: ['pool', 'spa', 'hot tub', 'maintenance'],
        suggestedCredentials: ['CPO Certification', 'Pool Contractor License'],
      },
      {
        id: 'contractor-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'contractor_specialty',
            label: 'What type of contractor work do you do?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // HEALTH & WELLNESS
  // -------------------------------------------------------------------------
  {
    id: 'healthcare',
    label: 'Health & Wellness',
    description: 'Medical, dental, therapy, and wellness services',
    icon: 'Heart',
    flowPattern: ['hero', 'services', 'about', 'credentials', 'testimonials', 'location', 'faq', 'cta', 'contact'],
    credentialsImportant: true,
    servicesLabel: 'Services',
    sectionLabels: {
      credentials: 'Credentials & Certifications',
      about: 'About Our Practice',
    },
    commonQuestions: [
      {
        id: 'accepts_insurance',
        label: 'Do you accept insurance?',
        type: 'boolean',
      },
      {
        id: 'insurance_accepted',
        label: 'Which insurance plans do you accept?',
        type: 'textarea',
        placeholder: 'e.g., Blue Cross, Aetna, United Healthcare...',
      },
      {
        id: 'telehealth',
        label: 'Do you offer telehealth/virtual appointments?',
        type: 'boolean',
      },
    ],
    types: [
      {
        id: 'dentist',
        label: 'Dentist',
        keywords: ['dental', 'dentistry', 'teeth', 'oral health'],
        suggestedCredentials: ['DDS/DMD', 'State License', 'ADA Member'],
      },
      {
        id: 'chiropractor',
        label: 'Chiropractor',
        keywords: ['chiropractic', 'spine', 'adjustment', 'back pain'],
        suggestedCredentials: ['DC License', 'Board Certified'],
      },
      {
        id: 'physical-therapist',
        label: 'Physical Therapist',
        keywords: ['physical therapy', 'rehabilitation', 'PT', 'recovery'],
        suggestedCredentials: ['DPT', 'State License', 'Board Certified'],
      },
      {
        id: 'massage-therapist',
        label: 'Massage Therapist',
        keywords: ['massage', 'bodywork', 'relaxation', 'therapeutic'],
        suggestedCredentials: ['LMT License', 'NCBTMB Certified'],
      },
      {
        id: 'counselor',
        label: 'Counselor / Therapist',
        keywords: ['counseling', 'therapy', 'mental health', 'psychology'],
        suggestedCredentials: ['LPC', 'LCSW', 'LMFT', 'State License'],
      },
      {
        id: 'acupuncturist',
        label: 'Acupuncturist',
        keywords: ['acupuncture', 'traditional chinese medicine', 'TCM'],
        suggestedCredentials: ['LAc License', 'NCCAOM Certified'],
      },
      {
        id: 'nutritionist',
        label: 'Nutritionist / Dietitian',
        keywords: ['nutrition', 'diet', 'wellness', 'health coaching'],
        suggestedCredentials: ['RD', 'RDN', 'CNS', 'State License'],
      },
      {
        id: 'optometrist',
        label: 'Optometrist',
        keywords: ['eye care', 'vision', 'glasses', 'contacts'],
        suggestedCredentials: ['OD', 'State License', 'AOA Member'],
      },
      {
        id: 'healthcare-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'healthcare_specialty',
            label: 'What type of healthcare service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // BEAUTY & PERSONAL CARE
  // -------------------------------------------------------------------------
  {
    id: 'beauty',
    label: 'Beauty & Personal Care',
    description: 'Salons, spas, and personal care services',
    icon: 'Sparkles',
    flowPattern: ['hero', 'services', 'gallery', 'testimonials', 'about', 'location', 'cta', 'contact'],
    galleryLabel: 'Our Work',
    servicesLabel: 'Services & Pricing',
    sectionLabels: {
      gallery: 'Portfolio',
    },
    commonQuestions: [
      {
        id: 'booking_system',
        label: 'How do clients book appointments?',
        type: 'select',
        options: ['Phone/Text', 'Online booking', 'Walk-ins welcome', 'By appointment only'],
      },
      {
        id: 'parking_info',
        label: 'Parking information',
        type: 'text',
        placeholder: 'e.g., Free street parking, lot behind building',
      },
    ],
    types: [
      {
        id: 'hair-salon',
        label: 'Hair Salon',
        keywords: ['hair', 'salon', 'stylist', 'cuts', 'color'],
        suggestedCredentials: ['Cosmetology License'],
      },
      {
        id: 'barbershop',
        label: 'Barbershop',
        keywords: ['barber', 'haircut', 'fade', 'beard'],
        suggestedCredentials: ['Barber License'],
      },
      {
        id: 'nail-salon',
        label: 'Nail Salon',
        keywords: ['nails', 'manicure', 'pedicure', 'nail art'],
        suggestedCredentials: ['Nail Technician License'],
      },
      {
        id: 'spa',
        label: 'Spa / Med Spa',
        keywords: ['spa', 'facial', 'skincare', 'wellness'],
        suggestedCredentials: ['Esthetician License', 'Medical Director (for med spa)'],
      },
      {
        id: 'makeup-artist',
        label: 'Makeup Artist',
        keywords: ['makeup', 'bridal', 'special occasion', 'beauty'],
        suggestedCredentials: ['Cosmetology License', 'Certified Makeup Artist'],
      },
      {
        id: 'lash-brow',
        label: 'Lash & Brow Studio',
        keywords: ['lashes', 'brows', 'extensions', 'microblading'],
        suggestedCredentials: ['Esthetician License', 'Lash Certification'],
      },
      {
        id: 'tattoo-piercing',
        label: 'Tattoo & Piercing',
        keywords: ['tattoo', 'piercing', 'body art', 'ink'],
        suggestedCredentials: ['Tattoo License', 'Bloodborne Pathogen Certified'],
      },
      {
        id: 'beauty-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'beauty_specialty',
            label: 'What type of beauty service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // FITNESS & RECREATION
  // -------------------------------------------------------------------------
  {
    id: 'fitness',
    label: 'Fitness & Recreation',
    description: 'Gyms, studios, trainers, and recreational activities',
    icon: 'Dumbbell',
    flowPattern: ['hero', 'services', 'testimonials', 'about', 'stats', 'gallery', 'location', 'cta', 'contact'],
    servicesLabel: 'Programs & Classes',
    galleryLabel: 'Our Facility',
    sectionLabels: {
      stats: 'By the Numbers',
    },
    commonQuestions: [
      {
        id: 'trial_offered',
        label: 'Do you offer a free trial or first session?',
        type: 'boolean',
      },
      {
        id: 'membership_types',
        label: 'What membership or pricing options do you offer?',
        type: 'textarea',
        placeholder: 'e.g., Monthly membership, class packs, drop-in rates...',
      },
    ],
    types: [
      {
        id: 'personal-trainer',
        label: 'Personal Trainer',
        keywords: ['personal training', 'fitness', 'workout', 'strength'],
        suggestedCredentials: ['NASM', 'ACE', 'ISSA', 'NSCA Certified'],
      },
      {
        id: 'yoga-studio',
        label: 'Yoga Studio',
        keywords: ['yoga', 'meditation', 'mindfulness', 'wellness'],
        suggestedCredentials: ['RYT-200', 'RYT-500', 'E-RYT'],
      },
      {
        id: 'pilates-studio',
        label: 'Pilates Studio',
        keywords: ['pilates', 'reformer', 'core', 'strength'],
        suggestedCredentials: ['PMA Certified', 'Balanced Body Certified'],
      },
      {
        id: 'gym',
        label: 'Gym / Fitness Center',
        keywords: ['gym', 'fitness center', 'workout', 'weights'],
      },
      {
        id: 'martial-arts',
        label: 'Martial Arts',
        keywords: ['martial arts', 'karate', 'jiu jitsu', 'taekwondo', 'mma'],
        suggestedCredentials: ['Black Belt', 'Instructor Certification'],
      },
      {
        id: 'dance-studio',
        label: 'Dance Studio',
        keywords: ['dance', 'ballet', 'hip hop', 'classes'],
      },
      {
        id: 'crossfit',
        label: 'CrossFit',
        keywords: ['crossfit', 'wod', 'functional fitness'],
        suggestedCredentials: ['CrossFit L1', 'CrossFit L2'],
      },
      {
        id: 'fitness-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'fitness_specialty',
            label: 'What type of fitness service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // FOOD & BEVERAGE
  // -------------------------------------------------------------------------
  {
    id: 'restaurant',
    label: 'Food & Beverage',
    description: 'Restaurants, cafes, bars, and food services',
    icon: 'UtensilsCrossed',
    flowPattern: ['hero', 'services', 'gallery', 'testimonials', 'about', 'location', 'cta', 'contact'],
    servicesLabel: 'Menu',
    galleryLabel: 'Our Food',
    sectionLabels: {
      services: 'Menu Highlights',
      location: 'Visit Us',
    },
    commonQuestions: [
      {
        id: 'cuisine_type',
        label: 'Type of cuisine',
        type: 'text',
        placeholder: 'e.g., Italian, Mexican, American comfort food',
      },
      {
        id: 'dietary_options',
        label: 'Dietary accommodations offered',
        type: 'multiselect',
        options: ['Vegetarian', 'Vegan', 'Gluten-free', 'Dairy-free', 'Nut-free', 'Halal', 'Kosher'],
      },
    ],
    types: [
      {
        id: 'restaurant-full-service',
        label: 'Restaurant - Full Service',
        keywords: ['restaurant', 'dining', 'fine dining', 'table service', 'dine-in'],
        questions: [
          {
            id: 'reservations',
            label: 'Do you take reservations?',
            type: 'select',
            options: ['Yes, recommended', 'Yes, for large parties', 'No, first come first served'],
          },
          {
            id: 'atmosphere',
            label: 'How would you describe your atmosphere?',
            type: 'select',
            options: ['Fine dining', 'Casual dining', 'Family-friendly', 'Romantic', 'Lively/Social'],
          },
        ],
      },
      {
        id: 'restaurant-quick-service',
        label: 'Restaurant - Quick Service',
        keywords: ['fast casual', 'quick service', 'counter service', 'takeout', 'fast food'],
        questions: [
          {
            id: 'ordering_methods',
            label: 'How can customers order? (select all that apply)',
            type: 'multiselect',
            options: ['Counter/register', 'Kiosk/self-service', 'Online/app', 'Drive-thru', 'Phone orders'],
          },
          {
            id: 'delivery_available',
            label: 'Do you offer delivery?',
            type: 'select',
            options: ['Yes, in-house delivery', 'Yes, through apps (DoorDash, Uber Eats, etc.)', 'No delivery'],
          },
        ],
      },
      {
        id: 'coffee-shop',
        label: 'Coffee Shop / Cafe',
        keywords: ['coffee', 'espresso', 'cafe', 'coffeehouse', 'barista'],
        questions: [
          {
            id: 'coffee_style',
            label: 'What best describes your coffee offerings?',
            type: 'select',
            options: ['Specialty/third-wave coffee', 'Traditional espresso bar', 'Quick-serve coffee', 'Coffee & food menu'],
          },
          {
            id: 'workspace_friendly',
            label: 'Is your space laptop/remote-work friendly?',
            type: 'boolean',
          },
        ],
      },
      {
        id: 'juice-smoothie',
        label: 'Juice Bar / Smoothie Shop',
        keywords: ['juice', 'smoothie', 'acai', 'health drinks', 'wellness'],
        questions: [
          {
            id: 'health_focus',
            label: 'What health benefits do you emphasize?',
            type: 'multiselect',
            options: ['Energy boost', 'Detox/cleanse', 'Protein/fitness', 'Immunity', 'Weight management'],
          },
          {
            id: 'organic',
            label: 'Do you use organic ingredients?',
            type: 'boolean',
          },
        ],
      },
      {
        id: 'bar-pub',
        label: 'Bar / Pub / Brewery',
        keywords: ['bar', 'pub', 'brewery', 'cocktails', 'beer', 'nightlife', 'taproom'],
        questions: [
          {
            id: 'bar_type',
            label: 'What type of establishment?',
            type: 'select',
            options: ['Cocktail bar', 'Sports bar', 'Neighborhood pub', 'Brewery/taproom', 'Wine bar', 'Nightclub'],
          },
          {
            id: 'serves_food',
            label: 'Do you serve food?',
            type: 'select',
            options: ['Full kitchen', 'Bar snacks/appetizers', 'No food'],
          },
        ],
      },
      {
        id: 'bakery-dessert',
        label: 'Bakery / Dessert Shop',
        keywords: ['bakery', 'bread', 'pastries', 'cakes', 'desserts', 'cupcakes', 'cookies'],
        questions: [
          {
            id: 'bakery_specialty',
            label: 'What do you specialize in?',
            type: 'multiselect',
            options: ['Fresh bread', 'Pastries', 'Custom cakes', 'Cupcakes', 'Cookies', 'Donuts', 'Ice cream/gelato'],
          },
          {
            id: 'custom_orders',
            label: 'Do you take custom orders?',
            type: 'boolean',
          },
        ],
      },
      {
        id: 'food-truck',
        label: 'Food Truck / Mobile Kitchen',
        keywords: ['food truck', 'mobile', 'street food', 'pop-up'],
        questions: [
          {
            id: 'schedule_type',
            label: 'How do customers find you?',
            type: 'select',
            options: ['Regular schedule/locations', 'Events only', 'Social media updates', 'App tracking'],
          },
          {
            id: 'private_events',
            label: 'Do you book private events?',
            type: 'boolean',
          },
        ],
      },
      {
        id: 'catering',
        label: 'Catering / Private Chef',
        keywords: ['catering', 'private chef', 'events', 'weddings', 'corporate catering'],
        questions: [
          {
            id: 'catering_types',
            label: 'What types of events do you cater?',
            type: 'multiselect',
            options: ['Weddings', 'Corporate events', 'Private parties', 'Drop-off catering', 'In-home dining'],
          },
          {
            id: 'minimum_guests',
            label: 'Minimum guest count (if any)',
            type: 'text',
            placeholder: 'e.g., 20 guests, no minimum',
          },
        ],
      },
      {
        id: 'food-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'food_specialty',
            label: 'What type of food business do you have?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // PROFESSIONAL SERVICES
  // -------------------------------------------------------------------------
  {
    id: 'professional',
    label: 'Professional Services',
    description: 'Legal, financial, consulting, and business services',
    icon: 'Briefcase',
    flowPattern: ['hero', 'services', 'about', 'credentials', 'testimonials', 'process', 'faq', 'cta', 'contact'],
    credentialsImportant: true,
    sectionLabels: {
      credentials: 'Qualifications',
      process: 'How We Work',
    },
    commonQuestions: [
      {
        id: 'consultation_offered',
        label: 'Do you offer free consultations?',
        type: 'boolean',
      },
      {
        id: 'client_types',
        label: 'Who are your typical clients?',
        type: 'textarea',
        placeholder: 'e.g., Small business owners, families, startups...',
      },
    ],
    types: [
      {
        id: 'lawyer',
        label: 'Lawyer / Attorney',
        keywords: ['law', 'legal', 'attorney', 'counsel'],
        suggestedCredentials: ['JD', 'State Bar License', 'Board Certified'],
        questions: [
          {
            id: 'practice_areas',
            label: 'Practice areas',
            type: 'multiselect',
            options: ['Family Law', 'Criminal Defense', 'Personal Injury', 'Business Law', 'Estate Planning', 'Immigration', 'Real Estate', 'Other'],
          },
        ],
      },
      {
        id: 'accountant',
        label: 'Accountant / CPA',
        keywords: ['accounting', 'CPA', 'tax', 'bookkeeping'],
        suggestedCredentials: ['CPA', 'EA', 'State License'],
      },
      {
        id: 'financial-advisor',
        label: 'Financial Advisor',
        keywords: ['financial planning', 'investments', 'wealth management'],
        suggestedCredentials: ['CFP', 'Series 7', 'Series 66', 'Fiduciary'],
      },
      {
        id: 'insurance-agent',
        label: 'Insurance Agent',
        keywords: ['insurance', 'coverage', 'policies', 'protection'],
        suggestedCredentials: ['State License', 'CLU', 'ChFC'],
      },
      {
        id: 'realtor',
        label: 'Real Estate Agent',
        keywords: ['real estate', 'realtor', 'homes', 'property'],
        suggestedCredentials: ['Real Estate License', 'NAR Member', 'Broker License'],
      },
      {
        id: 'consultant',
        label: 'Business Consultant',
        keywords: ['consulting', 'strategy', 'business', 'management'],
      },
      {
        id: 'marketing-agency',
        label: 'Marketing Agency',
        keywords: ['marketing', 'advertising', 'digital', 'branding'],
      },
      {
        id: 'professional-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'professional_specialty',
            label: 'What professional service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // CREATIVE & DESIGN
  // -------------------------------------------------------------------------
  {
    id: 'creative',
    label: 'Creative & Design',
    description: 'Photography, design, art, and creative services',
    icon: 'Palette',
    flowPattern: ['hero', 'gallery', 'services', 'about', 'testimonials', 'process', 'cta', 'contact'],
    galleryLabel: 'Portfolio',
    sectionLabels: {
      gallery: 'Featured Work',
      process: 'My Process',
    },
    commonQuestions: [
      {
        id: 'style_description',
        label: 'How would you describe your style?',
        type: 'textarea',
        placeholder: 'e.g., Modern and minimalist, bold and colorful, classic and timeless...',
      },
      {
        id: 'turnaround_time',
        label: 'Typical project turnaround time',
        type: 'text',
        placeholder: 'e.g., 2-3 weeks, 1 month, varies by project',
      },
    ],
    types: [
      {
        id: 'photographer',
        label: 'Photographer',
        keywords: ['photography', 'photos', 'portraits', 'events'],
        questions: [
          {
            id: 'photo_specialty',
            label: 'Photography specialty',
            type: 'multiselect',
            options: ['Weddings', 'Portraits', 'Events', 'Product', 'Real Estate', 'Headshots', 'Family', 'Commercial'],
          },
        ],
      },
      {
        id: 'videographer',
        label: 'Videographer',
        keywords: ['video', 'film', 'production', 'editing'],
      },
      {
        id: 'graphic-designer',
        label: 'Graphic Designer',
        keywords: ['graphic design', 'branding', 'logos', 'visual'],
      },
      {
        id: 'web-designer',
        label: 'Web Designer / Developer',
        keywords: ['web design', 'websites', 'development', 'digital'],
      },
      {
        id: 'interior-designer',
        label: 'Interior Designer',
        keywords: ['interior design', 'decor', 'spaces', 'renovation'],
        suggestedCredentials: ['NCIDQ', 'ASID Member'],
      },
      {
        id: 'artist',
        label: 'Artist',
        keywords: ['art', 'painting', 'sculpture', 'fine art'],
      },
      {
        id: 'musician',
        label: 'Musician / DJ',
        keywords: ['music', 'band', 'DJ', 'entertainment'],
      },
      {
        id: 'creative-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'creative_specialty',
            label: 'What type of creative work do you do?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // RETAIL & E-COMMERCE
  // -------------------------------------------------------------------------
  {
    id: 'retail',
    label: 'Retail & Shopping',
    description: 'Stores, boutiques, and retail businesses',
    icon: 'ShoppingBag',
    flowPattern: ['hero', 'services', 'gallery', 'testimonials', 'about', 'location', 'cta', 'contact'],
    servicesLabel: 'Products',
    galleryLabel: 'Shop',
    sectionLabels: {
      services: 'What We Offer',
      gallery: 'Featured Products',
    },
    commonQuestions: [
      {
        id: 'online_shopping',
        label: 'Do you offer online shopping?',
        type: 'boolean',
      },
      {
        id: 'brands_carried',
        label: 'Notable brands or products you carry',
        type: 'textarea',
        placeholder: 'e.g., Local artisans, Name brand products...',
      },
    ],
    types: [
      {
        id: 'boutique',
        label: 'Boutique / Clothing',
        keywords: ['boutique', 'clothing', 'fashion', 'apparel'],
      },
      {
        id: 'jewelry',
        label: 'Jewelry Store',
        keywords: ['jewelry', 'accessories', 'watches', 'gems'],
      },
      {
        id: 'gift-shop',
        label: 'Gift Shop',
        keywords: ['gifts', 'souvenirs', 'specialty', 'unique'],
      },
      {
        id: 'florist',
        label: 'Florist',
        keywords: ['flowers', 'florist', 'arrangements', 'plants'],
      },
      {
        id: 'pet-store',
        label: 'Pet Store / Services',
        keywords: ['pets', 'pet supplies', 'grooming', 'animals'],
      },
      {
        id: 'specialty-food',
        label: 'Specialty Food Store',
        keywords: ['specialty food', 'gourmet', 'organic', 'market'],
      },
      {
        id: 'retail-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'retail_specialty',
            label: 'What type of retail business do you have?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // EDUCATION & TRAINING
  // -------------------------------------------------------------------------
  {
    id: 'education',
    label: 'Education & Training',
    description: 'Tutoring, coaching, schools, and training services',
    icon: 'GraduationCap',
    flowPattern: ['hero', 'services', 'about', 'credentials', 'testimonials', 'process', 'faq', 'cta', 'contact'],
    credentialsImportant: true,
    servicesLabel: 'Programs',
    sectionLabels: {
      credentials: 'Qualifications',
      process: 'How It Works',
    },
    commonQuestions: [
      {
        id: 'age_groups',
        label: 'What age groups do you serve?',
        type: 'multiselect',
        options: ['Children (5-12)', 'Teens (13-17)', 'Adults (18+)', 'Seniors', 'All ages'],
      },
      {
        id: 'format',
        label: 'How are sessions delivered?',
        type: 'multiselect',
        options: ['In-person', 'Online/Virtual', 'Hybrid', 'Group classes', 'One-on-one'],
      },
    ],
    types: [
      {
        id: 'tutor',
        label: 'Tutor',
        keywords: ['tutoring', 'academic', 'test prep', 'homework'],
        questions: [
          {
            id: 'subjects',
            label: 'Subjects you teach',
            type: 'text',
            placeholder: 'e.g., Math, Science, SAT Prep, Spanish...',
          },
        ],
      },
      {
        id: 'music-teacher',
        label: 'Music Teacher',
        keywords: ['music lessons', 'piano', 'guitar', 'voice'],
        questions: [
          {
            id: 'instruments',
            label: 'Instruments you teach',
            type: 'text',
            placeholder: 'e.g., Piano, Guitar, Voice, Violin...',
          },
        ],
      },
      {
        id: 'language-school',
        label: 'Language School',
        keywords: ['language', 'ESL', 'foreign language', 'immersion'],
      },
      {
        id: 'driving-school',
        label: 'Driving School',
        keywords: ['driving', "driver's ed", 'lessons', 'license'],
        suggestedCredentials: ['State Certified', 'Licensed Instructor'],
      },
      {
        id: 'life-coach',
        label: 'Life Coach',
        keywords: ['coaching', 'personal development', 'motivation'],
        suggestedCredentials: ['ICF Certified', 'ACC', 'PCC'],
      },
      {
        id: 'trade-school',
        label: 'Trade School / Vocational',
        keywords: ['trade', 'vocational', 'certification', 'career'],
      },
      {
        id: 'education-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'education_specialty',
            label: 'What type of education or training do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // AUTOMOTIVE
  // -------------------------------------------------------------------------
  {
    id: 'automotive',
    label: 'Automotive',
    description: 'Auto repair, detailing, and vehicle services',
    icon: 'Car',
    flowPattern: ['hero', 'services', 'gallery', 'testimonials', 'credentials', 'about', 'location', 'cta', 'contact'],
    credentialsImportant: true,
    galleryLabel: 'Our Work',
    sectionLabels: {
      credentials: 'Certifications',
    },
    commonQuestions: [
      {
        id: 'brands_serviced',
        label: 'Vehicle brands you specialize in (if any)',
        type: 'text',
        placeholder: 'e.g., All makes, Import specialists, Ford/Chevy...',
      },
      {
        id: 'loaner_available',
        label: 'Do you offer loaner vehicles or shuttle service?',
        type: 'boolean',
      },
    ],
    types: [
      {
        id: 'auto-repair',
        label: 'Auto Repair Shop',
        keywords: ['auto repair', 'mechanic', 'car repair', 'maintenance'],
        suggestedCredentials: ['ASE Certified', 'AAA Approved'],
      },
      {
        id: 'auto-body',
        label: 'Auto Body / Collision',
        keywords: ['body shop', 'collision', 'paint', 'dent repair'],
        suggestedCredentials: ['I-CAR Certified', 'ASE Certified'],
      },
      {
        id: 'auto-detailing',
        label: 'Auto Detailing',
        keywords: ['detailing', 'car wash', 'ceramic coating', 'polish'],
        suggestedCredentials: ['IDA Certified'],
      },
      {
        id: 'tire-shop',
        label: 'Tire Shop',
        keywords: ['tires', 'wheels', 'alignment', 'balancing'],
      },
      {
        id: 'oil-change',
        label: 'Quick Lube / Oil Change',
        keywords: ['oil change', 'quick lube', 'maintenance'],
      },
      {
        id: 'towing',
        label: 'Towing Service',
        keywords: ['towing', 'roadside', 'emergency', 'recovery'],
      },
      {
        id: 'automotive-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'automotive_specialty',
            label: 'What type of automotive service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // EVENTS & ENTERTAINMENT
  // -------------------------------------------------------------------------
  {
    id: 'events',
    label: 'Events & Entertainment',
    description: 'Event planning, venues, and entertainment services',
    icon: 'PartyPopper',
    flowPattern: ['hero', 'services', 'gallery', 'testimonials', 'about', 'process', 'faq', 'cta', 'contact'],
    galleryLabel: 'Past Events',
    sectionLabels: {
      process: 'How We Work',
      gallery: 'Event Gallery',
    },
    commonQuestions: [
      {
        id: 'event_types',
        label: 'Types of events you handle',
        type: 'multiselect',
        options: ['Weddings', 'Corporate events', 'Birthday parties', 'Anniversaries', 'Graduations', 'Holiday parties', 'Non-profit galas'],
      },
      {
        id: 'capacity',
        label: 'Capacity or group size range',
        type: 'text',
        placeholder: 'e.g., 10-200 guests, up to 500 people',
      },
    ],
    types: [
      {
        id: 'wedding-planner',
        label: 'Wedding Planner',
        keywords: ['wedding', 'planner', 'coordinator', 'bridal'],
        suggestedCredentials: ['CWP', 'Certified Wedding Planner'],
      },
      {
        id: 'event-planner',
        label: 'Event Planner',
        keywords: ['events', 'planning', 'corporate', 'parties'],
        suggestedCredentials: ['CMP', 'CSEP'],
      },
      {
        id: 'venue',
        label: 'Event Venue',
        keywords: ['venue', 'banquet', 'reception', 'space'],
      },
      {
        id: 'dj-entertainment',
        label: 'DJ / Entertainment',
        keywords: ['DJ', 'music', 'entertainment', 'dance'],
      },
      {
        id: 'photo-booth',
        label: 'Photo Booth',
        keywords: ['photo booth', 'props', 'prints', 'party'],
      },
      {
        id: 'party-rental',
        label: 'Party Rentals',
        keywords: ['rentals', 'tents', 'tables', 'chairs', 'equipment'],
      },
      {
        id: 'events-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'events_specialty',
            label: 'What type of event service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // CLEANING & MAINTENANCE
  // -------------------------------------------------------------------------
  {
    id: 'cleaning',
    label: 'Cleaning & Maintenance',
    description: 'Residential and commercial cleaning services',
    icon: 'SprayCan',
    flowPattern: ['hero', 'services', 'testimonials', 'about', 'process', 'faq', 'cta', 'contact'],
    sectionLabels: {
      process: 'Our Process',
    },
    commonQuestions: [
      {
        id: 'service_area',
        label: 'Service area',
        type: 'text',
        placeholder: 'e.g., Austin metro area, within 30 miles of downtown',
        required: true,
      },
      {
        id: 'eco_friendly',
        label: 'Do you use eco-friendly/green products?',
        type: 'boolean',
      },
      {
        id: 'supplies_included',
        label: 'Do you bring your own supplies and equipment?',
        type: 'boolean',
      },
    ],
    types: [
      {
        id: 'house-cleaning',
        label: 'House Cleaning',
        keywords: ['house cleaning', 'maid service', 'residential', 'home'],
      },
      {
        id: 'commercial-cleaning',
        label: 'Commercial Cleaning',
        keywords: ['commercial', 'janitorial', 'office', 'business'],
      },
      {
        id: 'carpet-cleaning',
        label: 'Carpet Cleaning',
        keywords: ['carpet', 'upholstery', 'steam cleaning', 'stains'],
        suggestedCredentials: ['IICRC Certified'],
      },
      {
        id: 'window-cleaning',
        label: 'Window Cleaning',
        keywords: ['windows', 'glass', 'pressure washing'],
      },
      {
        id: 'pressure-washing',
        label: 'Pressure Washing',
        keywords: ['pressure washing', 'power washing', 'exterior'],
      },
      {
        id: 'junk-removal',
        label: 'Junk Removal',
        keywords: ['junk removal', 'hauling', 'cleanout', 'disposal'],
      },
      {
        id: 'cleaning-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'cleaning_specialty',
            label: 'What type of cleaning service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // PET SERVICES
  // -------------------------------------------------------------------------
  {
    id: 'pets',
    label: 'Pet Services',
    description: 'Pet care, grooming, training, and veterinary services',
    icon: 'PawPrint',
    flowPattern: ['hero', 'services', 'gallery', 'testimonials', 'about', 'credentials', 'location', 'faq', 'cta', 'contact'],
    galleryLabel: 'Happy Pets',
    sectionLabels: {
      gallery: 'Our Furry Clients',
    },
    commonQuestions: [
      {
        id: 'animals_served',
        label: 'What animals do you work with?',
        type: 'multiselect',
        options: ['Dogs', 'Cats', 'Birds', 'Small animals', 'Reptiles', 'Fish', 'Horses', 'All pets'],
      },
      {
        id: 'emergency_available',
        label: 'Do you offer emergency services?',
        type: 'boolean',
      },
    ],
    types: [
      {
        id: 'veterinarian',
        label: 'Veterinarian',
        keywords: ['vet', 'veterinary', 'animal hospital', 'pet doctor'],
        suggestedCredentials: ['DVM', 'State License', 'AAHA Accredited'],
      },
      {
        id: 'pet-grooming',
        label: 'Pet Grooming',
        keywords: ['grooming', 'pet salon', 'bathing', 'trimming'],
        suggestedCredentials: ['Certified Groomer', 'NDGAA'],
      },
      {
        id: 'dog-training',
        label: 'Dog Training',
        keywords: ['dog training', 'obedience', 'behavior', 'puppy'],
        suggestedCredentials: ['CPDT-KA', 'KPA Certified'],
      },
      {
        id: 'pet-sitting',
        label: 'Pet Sitting / Dog Walking',
        keywords: ['pet sitting', 'dog walking', 'pet care', 'boarding'],
        suggestedCredentials: ['Pet Sitter Certified', 'Bonded & Insured'],
      },
      {
        id: 'doggy-daycare',
        label: 'Doggy Daycare',
        keywords: ['daycare', 'boarding', 'kennel', 'play'],
      },
      {
        id: 'pets-other',
        label: 'Other',
        keywords: [],
        questions: [
          {
            id: 'pet_specialty',
            label: 'What type of pet service do you provide?',
            type: 'text',
            required: true,
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  // OTHER / GENERAL
  // -------------------------------------------------------------------------
  {
    id: 'other',
    label: 'Other',
    description: "My business doesn't fit these categories",
    icon: 'HelpCircle',
    flowPattern: ['hero', 'services', 'about', 'testimonials', 'gallery', 'cta', 'contact'],
    types: [
      {
        id: 'other-business',
        label: 'Other Business Type',
        keywords: [],
        questions: [
          {
            id: 'business_description',
            label: 'Please describe your business',
            type: 'textarea',
            required: true,
            placeholder: 'Tell us what your business does and who you serve...',
          },
          {
            id: 'industry_category',
            label: 'What industry would you say you\'re closest to?',
            type: 'text',
            placeholder: 'e.g., Technology, Agriculture, Non-profit...',
          },
        ],
      },
    ],
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get a category by ID
 */
export function getCategory(categoryId: string): IndustryCategory | undefined {
  return INDUSTRY_TAXONOMY.find((c) => c.id === categoryId);
}

/**
 * Get a specific industry type by category and type ID
 */
export function getIndustryType(
  categoryId: string,
  typeId: string
): IndustryType | undefined {
  const category = getCategory(categoryId);
  return category?.types.find((t) => t.id === typeId);
}

/**
 * Get the flow pattern for a category
 */
export function getFlowPatternForCategory(categoryId: string): SectionType[] {
  const category = getCategory(categoryId);
  return category?.flowPattern || INDUSTRY_TAXONOMY.find((c) => c.id === 'other')!.flowPattern;
}

/**
 * Get all questions for a category + type combination
 * Combines common category questions with type-specific questions
 */
export function getQuestionsForIndustry(
  categoryId: string,
  typeId: string
): IndustryQuestion[] {
  const category = getCategory(categoryId);
  if (!category) return [];

  const type = category.types.find((t) => t.id === typeId);
  const commonQuestions = category.commonQuestions || [];
  const typeQuestions = type?.questions || [];

  return [...commonQuestions, ...typeQuestions];
}

/**
 * Get section label overrides for a category
 */
export function getSectionLabels(
  categoryId: string
): Partial<Record<SectionType, string>> {
  const category = getCategory(categoryId);
  return category?.sectionLabels || {};
}

/**
 * Get suggested credentials for an industry type
 */
export function getSuggestedCredentials(
  categoryId: string,
  typeId: string
): string[] {
  const type = getIndustryType(categoryId, typeId);
  return type?.suggestedCredentials || [];
}

/**
 * Get keywords for AI profile generation
 */
export function getIndustryKeywords(
  categoryId: string,
  typeId: string
): string[] {
  const type = getIndustryType(categoryId, typeId);
  return type?.keywords || [];
}

/**
 * Check if credentials section should be prominent for this category
 */
export function isCredentialsImportant(categoryId: string): boolean {
  const category = getCategory(categoryId);
  return category?.credentialsImportant || false;
}
